import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import fp from 'lodash/fp'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { Heading, LoadingSpinner } from 'suomifi-ui-components'
import { LoadingSpinnerContainer } from 'todistukset-ui/src/vtj/components/price-list/PriceList.sc'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import type { TodistuksetPriceList } from 'todistukset-common/src/vtj/types/price-list.type'
import CertificateSubtypes from 'todistukset-common/src/vtj/CertificateSubtypes.enum'
import { TextBlockWithRows } from 'todistukset-ui/src/vtj/components/page-content/PageContent.sc'
import PriceListTable, {
  type PriceListTableRow,
} from 'todistukset-ui/src/vtj/components/price-list/PriceListTable.react'
import { StyledPriceListContainer } from 'todistukset-ui/src/vtj/components/price-list/PriceListTable.sc'

const PriceList: React.FC = observer(() => {
  const {
    priceList: { data, isLoading, refresh, error },
  } = useStore()
  const { t } = useTranslation()

  useEffect(() => {
    if (!data && !isLoading && !error) {
      refresh()
    }
  }, [refresh, data, isLoading, error])

  return (
    <TextBlockWithRows>
      <Heading variant={'h2'} as={'h3'}>
        <LocalizedString stringKey="priceListHeader" />
      </Heading>
      <LocalizedString stringKey={'priceListDescriptionWithAet'} />
      {isLoading || error || !data ? (
        <LoadingSpinnerContainer>
          <LoadingSpinner
            status={error ? 'failed' : 'loading'}
            text={t('priceListLoading')}
            textAlign="bottom"
            variant="small"
          />
        </LoadingSpinnerContainer>
      ) : (
        <PriceListContent priceListProducts={data.products} />
      )}
    </TextBlockWithRows>
  )
})

const PriceListContent: React.FC<{
  priceListProducts: TodistuksetPriceList['products']
}> = observer(({ priceListProducts }) => {
  const { t } = useTranslation()
  const { common: lang } = useStore()
  const productsWithSubType =
    getUniqueProductSubtypesFromPriceListProducts(priceListProducts)
  const [sahkoisetProducts, paperisetProducts] = fp.partition(
    (product) => product.productSubType === CertificateSubtypes.SAHKOINEN,
    productsWithSubType
  )

  const priceListTables: [PriceListTableRow, ...PriceListTableRow[]][] =
    useMemo(() => {
      function getPriceRow(product: {
        productSubType: CertificateSubtypes
        priceWithVatInEuroCents: number
      }): PriceListTableRow {
        return [
          {
            text: t(`productSubTypeTitleFor_${product.productSubType}`),
          },
          {
            price: product.priceWithVatInEuroCents,
            perUnit: t('instructionsStepPerson'),
          },
        ]
      }
      return [
        [
          [
            { text: t('priceListElectronicCertificate') },
            { text: t('priceListPrice') },
          ],
          ...sahkoisetProducts.map(getPriceRow),
        ],
        [
          [
            { text: t('priceListPaperCertificate') },
            { text: t('priceListPrice') },
          ],
          ...paperisetProducts.map(getPriceRow),
        ],
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sahkoisetProducts, paperisetProducts, lang])

  return (
    <StyledPriceListContainer data-test-id="price-list-content">
      <PriceListTable tables={priceListTables} />
    </StyledPriceListContainer>
  )
})

const getUniqueProductSubtypesFromPriceListProducts = (
  priceListProducts: TodistuksetPriceList['products']
) => {
  const productsWithSubtypes = priceListProducts
    .filter((product) => product.productType.includes('_'))
    .filter((product) => !product.productType.endsWith('ELOMAKE'))

  const groupedProducts = fp.groupBy(
    (product) => product.productType.split('_')[0],
    productsWithSubtypes
  )
  // Currently all the different todistus categories have the same prices, so just use any one of them
  // If the situation changes, the price list has to be designed and implemented in a different manner
  const [productTodistusType, products] = Object.entries(groupedProducts)[0]
  const subTypeRegex = new RegExp(`${productTodistusType}_?(.*)`)

  const productsWithSubType = products.map((product) => ({
    ...product,
    productSubType: (subTypeRegex.exec(product.productType) ??
      [])[1] as CertificateSubtypes,
  }))

  return productsWithSubType
}

export default PriceList
